import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '../generated/api';
import { trackUserJourney } from '../utils/datadog';
import { CREDIT_CARDS } from '../utils/constants';
import { getRumUser } from '../utils/users';

type BillPayRequest = components['schemas']['BillPayRequest'];
type BillPayResponse = components['schemas']['BillPayResponse'];

const BillPayPage: React.FC = () => {
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [creditCardData, setCreditCardData] = useState<BillPayResponse | null>(null);

  // Gets the RUM user id that was previously set during login
  const username = getRumUser();

  useEffect(() => {
    document.title = 'Bitsburg Bank - Pay Bill';
    const currentTimestamp = new Date().toISOString();

    trackUserJourney('billpay_page_visited', {
      previous_page: document.referrer || 'direct',
      timestamp: currentTimestamp,
    });

    // Check for URIError occurrence
    if (Math.random() < 0.1) {
      try {
        window.DD_LOGS?.logger?.info('Checking for URI error on bill pay page');
        decodeURIComponent('%');
      } catch (error) {
        window.DD_LOGS?.logger?.error('URI error on bill pay page', { error: error });
        window.DD_RUM?.addError(error);
      }
    }

    const fetchCreditCardBalance = async () => {
      try {
        const response = await fetch(`/api/billpay/balance?username=${encodeURIComponent(username)}`);
        const result = (await response.json()) as BillPayResponse;
        setCreditCardData(result);
        setAmount(result.balanceDue?.toString() || '0.00');

        // Check credit card data
        window.DD_LOGS?.logger?.info(
          'Credit card data accessed on bill pay page (Visa) ' + CREDIT_CARDS.VISA_FORMATTED
        );
        window.DD_RUM?.addAction('credit_card_data_accessed', {
          cardType: 'Visa',
          cardNumber: CREDIT_CARDS.VISA_FORMATTED,
        });
      } catch (error) {
        window.DD_LOGS?.logger?.error('Error fetching credit card balance', { error: error });
      }
    };

    fetchCreditCardBalance();
  }, [username]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const requestData: BillPayRequest = {
        username: username,
        creditCardNumber: creditCardData?.creditCardNumber || '',
        amount: parseFloat(amount),
      };

      const response = await fetch('/api/billpay/pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });

      const result = (await response.json()) as BillPayResponse;

      if (result.success) {
        trackUserJourney('credit_card_bill_payment', {
          credit_card_number: requestData.creditCardNumber,
          amount: requestData.amount,
          confirmation_number: result.confirmationNumber,
        });

        setSuccess(true);
        setConfirmationNumber(result.confirmationNumber || '');
      }
    } catch (error) {
      window.DD_LOGS?.logger?.error('Credit card bill payment error', { error: error });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="bank-card bank-card-no-hover">
                <div className="bank-card-body text-center">
                  <i className="bi bi-check-circle text-success display-4 mb-3"></i>
                  <h4 className="fw-bold mb-3">Credit Card Bill Payment Scheduled</h4>
                  <p>Your credit card bill payment of ${amount} has been scheduled.</p>
                  <p>
                    <strong>Confirmation:</strong> {confirmationNumber}
                  </p>
                  <Link to="/account" className="btn bank-btn-primary">
                    Back to Account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-5">
      <div className="container">
        <div className="row justify-content-center align-items-center min-vh-50">
          <div className="col-md-6">
            <div className="bank-card bank-card-no-hover">
              <div className="bank-card-header">
                <h4 className="mb-0 fw-bold">
                  <i className="bi bi-credit-card me-2"></i>
                  Pay Credit Card Bill
                </h4>
              </div>
              <div className="bank-card-body">
                {creditCardData && (
                  <div
                    className="mb-4 p-4 border border-2 border-datadog-purple rounded-3 bg-gradient"
                    style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}
                  >
                    <div className="d-flex align-items-center mb-3">
                      <i className="bi bi-credit-card-2-front text-datadog-purple fs-4 me-3"></i>
                      <div>
                        <h6 className="mb-1 fw-bold text-datadog-purple">Bitsburg Travel Rewards Card</h6>
                        <small className="text-muted">Primary Credit Card</small>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="mb-1 fw-semibold">XXXX-XXXX-XXXX-{creditCardData.creditCardNumber?.slice(-4)}</p>
                        <small className="text-muted">Card Number</small>
                      </div>
                      <div className="text-end">
                        <p className="mb-1 fw-bold text-danger fs-5">${creditCardData.balanceDue?.toFixed(2)}</p>
                        <small className="text-muted">Balance Due</small>
                      </div>
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Payment Amount</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>
                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn bank-btn-primary"
                      disabled={isSubmitting || !amount}
                      style={{ maxWidth: '200px', margin: '0 auto' }}
                      id="billpay-submit-btn"
                    >
                      {isSubmitting ? (
                        <span>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Processing...
                        </span>
                      ) : (
                        'Pay Bill'
                      )}
                    </button>
                    <Link
                      to="/account"
                      className="btn bank-btn-outline"
                      style={{ maxWidth: '200px', margin: '0 auto' }}
                    >
                      Cancel
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillPayPage;
